// core/draft — penyelamat ketikan saat mati lampu / internet putus (permintaan Apo 19 Jul).
//
// Cakupan SENGAJA dibatasi pada FORM yang panjang (Terima Servis): itu satu-satunya tempat di
// mana pekerjaan menit-menitan hilang total karena listrik padam. Isi form disimpan di perangkat
// tiap ketikan, dipulihkan saat aplikasi dibuka lagi, dan ditandai sebagai "belum selesai".
//
// TIDAK dibuat antrean tulis-offline untuk transaksi uang (serah terima, pembayaran, verifikasi):
// mengirim ulang perintah uang yang tersimpan berisiko DOBEL saat koneksi pulih — kerusakannya
// lebih besar daripada masalah yang dipecahkan. Uang tetap butuh server menjawab OK.
// ponytail: draft per-form di localStorage; naikkan ke IndexedDB bila kelak menyimpan lampiran foto.

import { kebijakan } from './settings';

const KEY = 'ifics-draft:';

export interface DraftMeta { at: string; label: string }

/** Simpan draft (dipanggil ter-debounce dari form). Gagal (storage penuh/privat) = diam — draft opsional. */
export function saveDraft(id: string, data: Record<string, unknown>, label: string): void {
  try {
    localStorage.setItem(KEY + id, JSON.stringify({ at: new Date().toISOString(), label, data }));
  } catch { /* storage penuh / mode privat → biarkan, form tetap jalan */ }
}

export function readDraft(id: string): { data: Record<string, unknown>; meta: DraftMeta } | null {
  try {
    const raw = localStorage.getItem(KEY + id);
    if (!raw) return null;
    const p = JSON.parse(raw) as { at: string; label: string; data: Record<string, unknown> };
    if (!p?.data || typeof p.data !== 'object') return null;
    // Draft yang ditinggalkan jangan menumpuk data pelanggan di perangkat. Lamanya settable
    // (Pengaturan → SLA & Waktu); 7 hari kalau setelan belum termuat.
    const maksHari = kebijakan().draftKedaluwarsaHari || 7;
    if (Date.now() - new Date(p.at).getTime() > maksHari * 86400000) { clearDraft(id); return null; }
    return { data: p.data, meta: { at: p.at, label: p.label } };
  } catch { return null; }
}

export const clearDraft = (id: string): void => { try { localStorage.removeItem(KEY + id); } catch { /* abaikan */ } };

/** Ada draft tersimpan? (untuk badge pengingat di layar). */
export const hasDraft = (id: string): boolean => !!readDraft(id);

/** Isi form dari draft: hanya elemen yang MASIH ADA (form berubah versi → sisanya dilewati). */
export function applyDraft(root: HTMLElement, data: Record<string, unknown>): void {
  for (const [id, v] of Object.entries(data)) {
    const el = root.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('#' + CSS.escape(id));
    if (!el) continue;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
      if (el.checked === !!v) continue;                       // nilainya sudah sama → jangan picu handler
      el.checked = !!v;
    } else {
      const baru = String(v ?? '');
      // WAJIB: memancarkan 'change' pada field yang nilainya TIDAK berubah bisa memicu render
      // ulang form (mis. pemilih cabang di Terima Servis) → isian yang baru dipulihkan ikut
      // terhapus. Ini bug nyata yang ketahuan saat uji: form kembali kosong setelah "Lanjutkan".
      if (el.value === baru) continue;
      el.value = baru;
    }
    // 'input' DULU: sebagian logika tampil/sembunyi (mis. kotak "DP diterima via") terikat ke
    // 'input', bukan 'change' — tanpa ini metode DP tersembunyi tapi ikut terkirim sebagai default
    // "Tunai", sehingga uang transfer tercatat sebagai uang laci.
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * Field yang TIDAK BOLEH masuk draft:
 *  - `f_sandi` = PIN/pola HP pelanggan. Draft yang ditinggal (mati lampu, staf pulang) akan
 *    menyimpannya di perangkat tanpa batas waktu — rahasia pelanggan, bukan milik kita.
 *  - `f_no`    = nomor servis (bertanggal). Draft kemarin dipulihkan hari ini → tiket bernomor
 *    tanggal lama, bahkan bisa bentrok dgn nomor yang sudah dipakai.
 */
const JANGAN_SIMPAN = new Set(['f_sandi', 'f_no']);

/** Kumpulkan nilai semua field ber-id di dalam `root` (checkbox → boolean). */
export function collectForm(root: HTMLElement): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input[id], select[id], textarea[id]').forEach((el) => {
    if (JANGAN_SIMPAN.has(el.id)) return;
    if (el instanceof HTMLInputElement && el.type === 'file') return;         // file tak bisa disimpan
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) out[el.id] = el.checked;
    else if (el.value) out[el.id] = el.value;
  });
  return out;
}

/** Pasang auto-simpan draft pada form (debounce 400ms). Kembalikan pembersih saat form ditutup. */
export function autosave(root: HTMLElement, id: string, label: string): () => void {
  let t = 0;
  const on = (): void => {
    window.clearTimeout(t);
    t = window.setTimeout(() => saveDraft(id, collectForm(root), label), 400);
  };
  root.addEventListener('input', on);
  root.addEventListener('change', on);
  return () => { window.clearTimeout(t); root.removeEventListener('input', on); root.removeEventListener('change', on); };
}

/** Status koneksi — dipakai form untuk memberi tahu kenapa simpan gagal & kapan bisa dicoba lagi. */
export const isOnline = (): boolean => navigator.onLine;
export function onBackOnline(fn: () => void): () => void {
  window.addEventListener('online', fn);
  return () => window.removeEventListener('online', fn);
}

/**
 * Boleh mengirim ulang otomatis saat koneksi pulih?
 *
 * Syaratnya ketat karena yang dipertaruhkan adalah TIKET GANDA dan isian yang hilang:
 *  - hanya bila simpan terakhir memang gagal karena koneksi (`snapshot` terisi),
 *  - hanya bila layar yang sama masih terbuka (`layarHidup`),
 *  - dan hanya bila isiannya BELUM diubah sesudah gagal — kalau staf sempat mengoreksi,
 *    mengirim diam-diam berarti menyimpan versi lama lalu mengosongkan form, sehingga
 *    koreksinya ikut hilang. Dalam hal itu tampilkan tombol, jangan kirim sendiri.
 */
export function bolehKirimOtomatis(snapshot: string | null, isiSekarang: Record<string, unknown>, layarHidup: boolean): boolean {
  if (!layarHidup || snapshot == null) return false;
  return JSON.stringify(isiSekarang) === snapshot;
}
